import React, { useState, useEffect, useCallback, useMemo } from "react";
import CardTable from "components/CardTable/CardTable";
import { TableInputs } from "components/CardTable/TableInputs";
import { Autocomplete, InputLabel, TextField } from "@mui/material";

import { useDispatch, useSelector } from "react-redux";

import { columns } from "./columns";

import { Home } from "@mui/icons-material";
import { useSearch } from "hooks/useSearch";
import { useToggle } from "hooks/useToggle";
import { NOTES_FILTERS, NOTES_PHRASE } from "./config";
import { Modal } from "components/Modal/Modal";
import {
  fetchNotes,
  sendNote,
  clearNotes,
  getFile,
  applyPayment,
  reassignPayment,
  handleNotesBuildingsInputsAction
} from "redux/actions/buildings/notes/notesActions";
import { useTimer } from "hooks/useTimer";

const LOADINGS = ["loadingNotes", "loadingSendNote", "loadingFile"];
const TABLE_KEY = "notesBuildings";
const PAYMENT_OPTIONS = [{ id: "BANAMEX", name: "Banamex" },
{ id: "BANK_CHECK", name: "Cheque" },
{ id: "CASH", name: "Efectivo" },
{ id: "PAYMENT_LINK", name: "Liga de pago" },
{ id: "NOTE", name: "Nota" },
{ id: "DEBIT_CARD", name: "Tarjeta Débito" },
{ id: "CREDIT_CARD", name: "Tarjeta Crédito" },
{ id: "TRANSFER", name: "Transferencia" }]

const CANCEL_OPTIONS = [
  { id: "CANCEL", name: "CANCELAR" },
  { id: "REASSIGN", name: "REASIGNAR" }
]

export const NotesPage = () => {
  const dispatch = useDispatch();
  const [fileModal, setFileModal] = useToggle();
  const [confirmationModal, setConfirmationModal] = useToggle();
  const [paymentModal, setPaymentModal] = useToggle();
  const [reassignModal, setReassignModal] = useToggle();
  const [filtersTimer] = useTimer(800);

  const {
    setSnackbar,
    clearSearch,
    buildCompLoadings,
    handleFilter: setFilter
  } = useSearch();

  const { rules } = useSelector(({ login }) => login)

  const { isLoadingComp } = useSelector(
    state => state.search
  );
  const { notes, station } = useSelector(state => state.notesBuildings);
  const { configTables } = useSelector(({ search }) => search);

  const [selectedNote, setSelectedNote] = useState({})
  const [idNote, setIdNote] = useState("");
  const [idOrigin, setIdOrigin] = useState("");
  const [idReassign, setIdReassign] = useState("");
  const [paymentReference, setPaymentReference] = useState({})

  const handleOpenConfirmation = note => {
    setConfirmationModal();
    setIdNote(note);
  };
  const handleOpenFile = note => {
    setFileModal();
    setIdNote(note);
  };

  const handleOpenPaymentLink = linkUrl => {
    try {
      navigator.clipboard.writeText(linkUrl.toString()).then(() => {
        setSnackbar("Liga copiada al portapapeles", "success");
      });
    } catch (ex) {
      setSnackbar("Error al copiar la liga");
    }
  };

  const handleOpenPayment = (note, reference) => {
    setPaymentModal();
    setIdNote(note);
    setIdOrigin(null);
    setPaymentReference(reference);
    setSelectedNote(notes.find(obj => obj.id === note))
  };

  const handleOpenReassign = (note, reference) => {
    setReassignModal();
    setPaymentReference(reference);
    setIdNote(note);
    setSelectedNote(notes.find(obj => obj.id === note))
  }

  const notesMemo = useMemo(
    () =>
      notes.map(note => {
        return {
          ...note,
          confirmation: handleOpenConfirmation,
          file: handleOpenFile,
          paymentUrl: handleOpenPaymentLink,
          payment: handleOpenPayment,
          reassign: handleOpenReassign,
          rules
        };
      }),
    [notes]
  );

  const getNotes = useCallback(
    (stationID, page, size, tableKey, filters) => {
      dispatch(fetchNotes(stationID, page, size, tableKey, filters));
    },
    [dispatch]
  );

  const handleSubmit = useCallback(() => {
    if (station.length < 1) {
      setSnackbar(NOTES_PHRASE.search.messages.noStations);
      return;
    }
    const stationID = station.id;

    getNotes(stationID, 0, 10, TABLE_KEY, []);
  }, [station, setSnackbar]);

  const handleNextPage = useCallback(
    (page, tableKey) => {
      const stationID = station.id;
      const size = configTables[tableKey].pageSize;
      const requiredFilters = configTables[tableKey].filters
        ? configTables[tableKey].filters.filter(filter =>
          NOTES_FILTERS.includes(filter.id)
        )
        : [];

      getNotes(stationID, page, size, tableKey, requiredFilters);
    },
    [station, configTables]
  );

  const handleChangePageSize = useCallback(
    (size, tableKey) => {
      const stationID = station.id;
      const page = configTables[tableKey].page;
      const requiredFilters = configTables[tableKey].filters
        ? configTables[tableKey].filters.filter(filter =>
          NOTES_FILTERS.includes(filter.id)
        )
        : [];

      getNotes(stationID, page, size, tableKey, requiredFilters);
    },
    [station, configTables]
  );

  const handleFilter = e => {
    if (Array.isArray(e)) {
      const requiredFilters = e.filter(filter =>
        NOTES_FILTERS.includes(filter.id)
      );
      const size = configTables[TABLE_KEY].pageSize;
      filtersTimer(getNotes, [
        station.id,
        0,
        size,
        TABLE_KEY,
        requiredFilters
      ]);
      setFilter(e, TABLE_KEY);
    }
  };

  const handleSendNote = useCallback(() => {
    dispatch(sendNote(idNote, setConfirmationModal));
  }, [idNote]);

  const handleGetReport = useCallback(() => {
    dispatch(getFile(idNote, setFileModal));
  }, [idNote]);

  const submitMemoized = useCallback((e) => {
    e.preventDefault();
    handleSubmit();
  }, [handleSubmit]);

  const handlePayment = useCallback(() => {
    dispatch(applyPayment(idNote, idOrigin, paymentReference, () => { setPaymentModal(); handleSubmit(); }));
  }, [idNote, idOrigin, paymentReference]);

  const handleReassign = useCallback(() => {
    dispatch(reassignPayment(idNote, idOrigin, idReassign, paymentReference, () => { setReassignModal(); handleSubmit(); }));
  }, [idNote, idOrigin, idReassign, paymentReference]);

  const handleChangeOrigin = (e, v) => {
    setIdOrigin(v.id);
  };

  const handleChangeReassign = (e, v) => {
    setIdReassign(v.id)
  }

  useEffect(() => {
    clearSearch();
    dispatch(clearNotes());
  }, [clearSearch]);

  useEffect(() => {
    buildCompLoadings(LOADINGS);
  }, []);

  return (
    <React.Fragment>
      <CardTable
        data={notesMemo}
        columns={columns}
        loading={isLoadingComp?.loadingNotes}
        title={NOTES_PHRASE.page.table.title}
        icon={<Home />}
        tableKey={TABLE_KEY}
        onPage={handleNextPage}
        onPageSize={handleChangePageSize}
        onFilter={handleFilter}
        hasHeader
        blockBtn
        manual
        tableContent={
          <TableInputs
            reducer="notesBuildings"
            stations
            dispatchAction={handleNotesBuildingsInputsAction}
            submitAction={submitMemoized}
          />
        }
      />
      <Modal
        isOpen={confirmationModal}
        handleClose={setConfirmationModal}
        textButton1={NOTES_PHRASE.modal.actions.send}
        textButton2={NOTES_PHRASE.modal.actions.cancel}
        actionFunction1={setConfirmationModal}
        actionFunction2={handleSendNote}
        textContent={NOTES_PHRASE.modal.content.send}
        isDisabled={isLoadingComp.loadingSendNote}
      />
      <Modal
        isOpen={fileModal}
        handleClose={setFileModal}
        textButton1={NOTES_PHRASE.modal.actions.file}
        textButton2={NOTES_PHRASE.modal.actions.cancel}
        actionFunction1={setFileModal}
        actionFunction2={handleGetReport}
        textContent={NOTES_PHRASE.modal.content.file}
        isDisabled={isLoadingComp.loadingFile}
      />
      <Modal
        isOpen={paymentModal}
        handleClose={setPaymentModal}
        textButton1={NOTES_PHRASE.modal.actions.payment}
        textButton2={NOTES_PHRASE.modal.actions.cancel}
        actionFunction1={setPaymentModal}
        actionFunction2={handlePayment}
        textContent={NOTES_PHRASE.modal.content.payment(selectedNote)}
        isDisabled={isLoadingComp.applyPayment || !idOrigin}
        children={
          <Autocomplete
            options={PAYMENT_OPTIONS}
            key={paymentModal}
            disableClearable
            getOptionLabel={option => option.name}
            renderInput={params => (
              <TextField
                {...params}
                name="cancelMotives"
                label={NOTES_PHRASE.modal.dropdown.label}
                variant="outlined"
                size="small"
              />
            )}
            fullWidth
            onChange={(e, v) => handleChangeOrigin(e, v)}
            noOptionsText={NOTES_PHRASE.modal.dropdown.noOptions}
          />
        }
      />
      <Modal
        isOpen={reassignModal}
        handleClose={setReassignModal}
        textButton1={NOTES_PHRASE.modal.actions.reassign}
        textButton2={NOTES_PHRASE.modal.actions.cancel}
        actionFunction1={setReassignModal}
        actionFunction2={handleReassign}
        textContent={NOTES_PHRASE.modal.content.reassign}
        isDisabled={isLoadingComp.reassignPayment || !idReassign || (idReassign == 'REASSIGN' && !idOrigin)}
        children={
          <>
            <Autocomplete
              options={CANCEL_OPTIONS}
              key={paymentModal}
              disableClearable
              getOptionLabel={option => option.name}
              renderInput={params => (
                <TextField
                  {...params}
                  name="reassignOptions"
                  label={NOTES_PHRASE.modal.dropdown.label}
                  variant="outlined"
                  size="small"
                />
              )}
              fullWidth
              onChange={(e, v) => handleChangeReassign(e, v)}
              noOptionsText={NOTES_PHRASE.modal.dropdown.reassign.noOptions}
            />
            {idReassign == 'REASSIGN' &&
              <>
                <InputLabel sx={{ margin: '10px 0 5px 0' }}>Seleccione nuevo origen:</InputLabel>
                <Autocomplete
                  options={PAYMENT_OPTIONS}
                  key={paymentModal}
                  disableClearable
                  getOptionLabel={option => option.name}
                  renderInput={params => (
                    <TextField
                      {...params}
                      name="cancelMotives"
                      label={NOTES_PHRASE.modal.dropdown.label}
                      variant="outlined"
                      size="small"
                    />
                  )}
                  fullWidth
                  onChange={(e, v) => handleChangeOrigin(e, v)}
                  noOptionsText={NOTES_PHRASE.modal.dropdown.noOptions}
                />
              </>
            }
          </>
        }
      />
    </React.Fragment>
  );
};
